/**
 * StatCheckRenderer.ts — Deterministic stat check visualization.
 *
 * Renders stat-based checks for the hybrid system:
 * - Gated choices: some options require a stat threshold to appear
 * - Outcome variation: checks always resolve, but narrative text
 *   varies based on whether the player's stat meets the threshold
 *
 * No dice, no randomness — your character build determines the outcome.
 */

import type { Terminal } from './Terminal';
import type { InputManager } from './InputManager';
import type { ColorName } from './ColorTheme';
import { theme } from './ColorTheme';
import { formatStatPeek, calculateModifier } from './StatPeek';

// --- Types ---

export interface StatCheckDisplay {
  /** Stat name (e.g., "Strength") */
  statName: string;
  /** Required stat value to pass */
  threshold: number;
  /** Player's current stat value */
  playerValue: number;
}

export interface StatCheckResult {
  statName: string;
  threshold: number;
  playerValue: number;
  passed: boolean;
}

// --- StatCheckRenderer ---

export class StatCheckRenderer {
  private terminal: Terminal;
  private inputManager: InputManager;

  private static readonly BOX_WIDTH = 40;

  constructor(terminal: Terminal, inputManager: InputManager) {
    this.terminal = terminal;
    this.inputManager = inputManager;
  }

  /**
   * Render a stat check with a brief dramatic pause before the result.
   * Returns the check result for the caller to branch on.
   */
  async render(check: StatCheckDisplay): Promise<StatCheckResult> {
    const { statName, threshold, playerValue } = check;
    const passed = playerValue >= threshold;
    const modifier = calculateModifier(playerValue);
    const modStr = modifier >= 0 ? `+${modifier}` : `${modifier}`;
    const w = StatCheckRenderer.BOX_WIDTH;

    this.terminal.blankLine();
    this.drawBoxTop(w);
    this.drawBoxLine(`STAT CHECK: ${statName}`, w, 'yellow');
    this.drawBoxLine(`Required: ${threshold}  |  Yours: ${playerValue} (${modStr})`, w, 'white');
    this.drawBoxMid(w);

    // Brief dramatic pause (skippable)
    await this.pause(500);

    // Result with margin
    const margin = Math.abs(playerValue - threshold);
    if (passed) {
      this.drawBoxLine(`${theme.getSymbolPrefix('green')}\u2713 SUCCESS`, w, 'green');
      if (margin > 0) {
        this.drawBoxLine(`Exceeded requirement by ${margin}`, w, 'white');
      } else {
        this.drawBoxLine(`Barely passed!`, w, 'white');
      }
    } else {
      this.drawBoxLine(`${theme.getSymbolPrefix('red')}\u2717 FAILED`, w, 'red');
      this.drawBoxLine(`Missed requirement by ${margin}`, w, 'white');
    }
    this.drawBoxBottom(w);
    this.terminal.blankLine();

    return { statName, threshold, playerValue, passed };
  }

  /**
   * Render a stat check result instantly (no pause).
   */
  renderInstant(check: StatCheckDisplay): StatCheckResult {
    const { statName, threshold, playerValue } = check;
    const passed = playerValue >= threshold;
    const modifier = calculateModifier(playerValue);
    const modStr = modifier >= 0 ? `+${modifier}` : `${modifier}`;
    const w = StatCheckRenderer.BOX_WIDTH;

    this.terminal.blankLine();
    this.drawBoxTop(w);
    this.drawBoxLine(`STAT CHECK: ${statName}`, w, 'yellow');
    this.drawBoxLine(`Required: ${threshold}  |  Yours: ${playerValue} (${modStr})`, w, 'white');
    const margin = Math.abs(playerValue - threshold);
    if (passed) {
      this.drawBoxLine(`${theme.getSymbolPrefix('green')}\u2713 SUCCESS`, w, 'green');
      if (margin > 0) {
        this.drawBoxLine(`Exceeded requirement by ${margin}`, w, 'white');
      } else {
        this.drawBoxLine(`Barely passed!`, w, 'white');
      }
    } else {
      this.drawBoxLine(`${theme.getSymbolPrefix('red')}\u2717 FAILED`, w, 'red');
      this.drawBoxLine(`Missed requirement by ${margin}`, w, 'white');
    }
    this.drawBoxBottom(w);
    this.terminal.blankLine();

    return { statName, threshold, playerValue, passed };
  }

  /**
   * Format a stat requirement string for display in choice menus.
   * Used by the story engine to annotate gated choices.
   *
   * Returns formatted text with color based on pass/fail status.
   * Uses StatPeek formatting for consistent display.
   */
  formatGate(statName: string, threshold: number, playerValue: number): { text: string; color: ColorName } {
    const abbr = statName.substring(0, 3).toUpperCase();
    const result = formatStatPeek({
      statAbbrev: abbr,
      statValue: playerValue,
      threshold,
    });
    return {
      text: result.text,
      color: result.color,
    };
  }

  // --- Helpers ---

  /** Skippable pause for dramatic effect. */
  private pause(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      let resolved = false;

      const previousMode = this.inputManager.getMode();
      this.inputManager.setMode('anyKey');

      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          this.inputManager.off(handler);
          this.inputManager.setMode(previousMode);
          resolve();
        }
      }, ms);

      const handler = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          this.inputManager.off(handler);
          this.inputManager.setMode(previousMode);
          resolve();
        }
      };

      this.inputManager.on(handler);
    });
  }

  // --- Box Drawing ---

  private drawBoxTop(width: number): void {
    const inner = '\u2550'.repeat(width - 2);
    this.terminal.writeLine(`  \u2554${inner}\u2557`, 'white');
  }

  private drawBoxMid(width: number): void {
    const inner = '\u2550'.repeat(width - 2);
    this.terminal.writeLine(`  \u2560${inner}\u2563`, 'white');
  }

  private drawBoxBottom(width: number): void {
    const inner = '\u2550'.repeat(width - 2);
    this.terminal.writeLine(`  \u255A${inner}\u255D`, 'white');
  }

  private drawBoxLine(text: string, boxWidth: number, color: ColorName): void {
    const inner = boxWidth - 4;
    const padded = text.length <= inner
      ? text + ' '.repeat(inner - text.length)
      : text.substring(0, inner);
    this.terminal.write('  \u2551 ', 'white');
    this.terminal.write(padded, color);
    this.terminal.writeLine(' \u2551', 'white');
  }
}
